import {
  Badge,
  Body,
  Box,
  Flex,
  Grid,
  Heading,
  IconButton,
  Image,
  useToast,
} from '@hover/blueprint';
import { iconClose } from '@hover/icons';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MeasurementTitle } from 'src/modules/homeownerApp/components/Projects/Project/Measurements';
import {
  MEASUREMENT_FOCUS_ELEMENTS,
  Measurements,
} from 'src/modules/homeownerApp/components/Projects/Project/Measurements';
import { ThreeDModel } from 'src/modules/homeownerApp/components/ThreeDModel';
import { useErrors, useFormattedTimeAgo } from 'src/modules/homeownerApp/hooks';
import { useRemoveScanFromProjectHandler } from 'src/modules/homeownerApp/hooks/useRemoveScanFromProjectHandler';
import { Button } from 'src/shared/components';
import type { BasicJobInfoFragment } from 'src/shared/graphql/generated';
import {
  JobModelStates,
  useGetJobHomeownerMeasurementsQuery,
} from 'src/shared/graphql/generated';
import { hideScrollbars } from 'src/shared/helpers/styles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { logError } from 'src/shared/telemetry/logger';

import { useDeleteScanHandler } from '../../hooks/useDeleteScanHandler';

const SCAN_DETAILS_PADDING = { base: 500, tablet: 1000 };

type InfoTileProps = {
  label: string;
  value: string;
};

type ScanDetailsProps = {
  scan: BasicJobInfoFragment;
  projectId?: number;
  onClose: () => void;
};

// UPLOADING, PROCESSING_UPLOAD, WAITING_APPROVAL, PAYING, COMPLETE, FAILED, CANCELLED
const canDeleteScan = (scan: BasicJobInfoFragment) => {
  switch (scan.state) {
    case JobModelStates.Uploading:
    case JobModelStates.ProcessingUpload:
    case JobModelStates.WaitingApproval:
    case JobModelStates.Paying:
    case JobModelStates.Complete:
    case JobModelStates.Failed:
    case JobModelStates.Cancelled:
      return true;
    default:
      return false;
  }
};

const getBadgeTranslationKey = (cubeFace: number) => {
  switch (cubeFace) {
    case 0:
      return 'scan.cubeFace.0';
    case 1:
      return 'scan.cubeFace.1';
    case 2:
      return 'scan.cubeFace.2';
    case 3:
      return 'scan.cubeFace.3';
    case 4:
      return 'scan.cubeFace.4';
    case 5:
      return 'scan.cubeFace.5';
    case 6:
      return 'scan.cubeFace.6';
    case 7:
      return 'scan.cubeFace.7';
    default:
      return 'scan.cubeFace.misc';
  }
};

const InfoTile = ({ label, value }: InfoTileProps) => (
  <Flex direction="column">
    <Heading
      color="neutral.500"
      m={0}
      textStyle="heading100"
      whiteSpace="nowrap"
    >
      {label}
    </Heading>
    <Heading m={0} textStyle="heading100" whiteSpace="nowrap">
      {value}
    </Heading>
  </Flex>
);

const BoxHideScroll = hideScrollbars(Box);

export const ScanDetails = ({ scan, projectId, onClose }: ScanDetailsProps) => {
  const { i18n } = useTranslation();
  const toast = useToast();
  const { showError } = useErrors();
  const currentLocale = i18n.language;
  const showRemoveScan = !!projectId;
  const disableButton = !showRemoveScan && !canDeleteScan(scan);

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [activeItem, setActiveItem] = React.useState<MeasurementTitle | null>(
    null,
  );
  const { data, error } = useGetJobHomeownerMeasurementsQuery({
    variables: {
      id: scan.id,
    },
  });

  const [removeScanHandler, removeScanResult] = useRemoveScanFromProjectHandler(
    Number(projectId),
    scan,
  );

  const handleRemoveScan = () => {
    removeScanHandler({
      onSuccess: () => {
        onClose();
        toast({
          props: {
            message: t('customize.newProject.removeScanSuccess'),
            colorScheme: 'dark',
          },
        });
      },
      onError: () => {
        showError({
          error: removeScanResult.error as Error,
          formatError: message =>
            t('projects.confirmation.removeScan.error', { message }),
        });
      },
    });
  };

  const [deleteScanHandler, deleteScanResult] = useDeleteScanHandler(scan.id);

  const handleDeleteScan = () => {
    deleteScanHandler({
      onSuccess: () => {
        onClose();
        toast({
          props: {
            message: t('projects.confirmation.deleteScan.success'),
            colorScheme: 'dark',
          },
        });
      },
      onError: () => {
        showError({
          error: deleteScanResult.error as Error,
          formatError: message =>
            t('projects.confirmation.deleteScan.error', { message }),
        });
      },
    });
  };

  useEffect(() => {
    if (error) logError(error);
  }, [error]);

  // Reset active item to be null when jobId changes
  useEffect(() => {
    setActiveItem(null);
  }, [scan.id]);

  const paintData = data?.jobs?.results[0];
  const fullJson = paintData?.homeownerMeasurements?.fullJson;
  const threeDFiles = paintData?.threeDFiles;

  const focusElement = activeItem
    ? MEASUREMENT_FOCUS_ELEMENTS[activeItem]
    : undefined;
  const addedDaysAgo = useFormattedTimeAgo(scan.createdAt);
  const localeDateString = useMemo(() => {
    if (!scan.createdAt) return '';

    // Convert the date to a localized string
    return new Date(scan.createdAt).toLocaleDateString(currentLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [scan.createdAt, currentLocale]);

  return (
    <Flex direction="column">
      <Flex backgroundColor="neutral.100" direction="column" pt="800">
        <Flex direction="row-reverse" px={SCAN_DETAILS_PADDING}>
          <Flex
            position={{ base: 'unset', tablet: 'absolute' }}
            right={700}
            top={700}
          >
            <IconButton
              icon={iconClose}
              label="close"
              onClick={onClose}
              variant="ghost"
            />
          </Flex>
          <Flex direction="column" flex={1}>
            <Heading m={0} mb={200} textStyle="heading100">
              {t('projects.scan.details.scanArea', { area: 'Exterior' })}
            </Heading>
            <Heading mb={400} noOfLines={1} textStyle="heading700">
              {scan.name}
            </Heading>
          </Flex>
        </Flex>
        <BoxHideScroll
          gap={900}
          mb={400}
          overflow="auto"
          px={SCAN_DETAILS_PADDING}
          scrollSnapType="x mandatory"
          width="100%"
        >
          <InfoTile
            label={t('projects.scan.details.address')}
            value={scan.locationLine1 ?? ''}
          />
          {localeDateString && (
            <InfoTile
              label={t('projects.scan.details.scanDate')}
              value={localeDateString}
            />
          )}
          <InfoTile label={t('projects.scan.details.area')} value="Exterior" />
          <InfoTile
            label={t('projects.scan.details.jobId')}
            value={scan.id.toString()}
          />
          <InfoTile
            label={t('projects.scan.details.numOfPhotos')}
            value={scan.images.length.toString()}
          />
        </BoxHideScroll>
        <Flex direction="column">
          {threeDFiles && (
            <ThreeDModel
              focusElement={focusElement}
              threeDFiles={threeDFiles}
            />
          )}
        </Flex>
      </Flex>
      <Flex
        backgroundColor="neutral.0"
        direction="column"
        flex={1}
        gap={600}
        pb={800}
        pt={700}
      >
        <Heading m={0} px={SCAN_DETAILS_PADDING} textStyle="heading100">
          {t('projects.keyMeasurements.title')}
        </Heading>
        <Measurements
          activeItem={activeItem}
          fullJson={fullJson}
          jobId={scan.id}
          projectId={projectId}
          px={SCAN_DETAILS_PADDING}
          setActiveItem={setActiveItem}
        />
      </Flex>
      <Flex
        direction="column"
        pb={{
          base: 'calc(var(--motion-overflow) + var(--hover-space-800))',
          tablet: '800',
        }}
        px={SCAN_DETAILS_PADDING}
      >
        <Box flexDirection="column" mb="500">
          <Heading m={0} textStyle="heading100">
            {t('scan.allScannedPhotos', { numOfPhotos: scan.images.length })}
          </Heading>
          <Body color="neutral.500" m={0} textStyle="bodySmall">
            {t('scan.added', { daysAgo: addedDaysAgo })}
          </Body>
        </Box>
        <Grid gap={{ base: 200, tablet: 500 }} templateColumns="repeat(2, 1fr)">
          {scan.images.map(image => (
            <Flex
              borderRadius="400"
              key={image.id}
              overflow="hidden"
              position="relative"
              sx={{ aspectRatio: 1 }}
            >
              <Image
                alt="scan image"
                height="100%"
                key={image.id}
                objectFit="cover"
                src={image.rotatedImageMediumUrl ?? ''}
                width="100%"
              />
              {image.cubeFace && (
                <Badge
                  backgroundColor="neutral.900"
                  borderRadius="200"
                  bottom={{ base: 200, tablet: 400 }}
                  left={{ base: 200, tablet: 400 }}
                  position="absolute"
                  px={200}
                  py={100}
                  textTransform="unset"
                >
                  {t(getBadgeTranslationKey(image.cubeFace))}
                </Badge>
              )}
            </Flex>
          ))}
        </Grid>
        <Box pt={800}>
          <Button
            backgroundColor={projectId ? undefined : 'danger.600'}
            disabled={disableButton}
            onClick={projectId ? handleRemoveScan : handleDeleteScan}
            primaryCallToAction
            size="large"
            variant={projectId ? 'tertiary' : 'primary'}
            width="1"
          >
            {projectId
              ? t('projects.scan.removeScan')
              : t('projects.confirmation.deleteScan.cta')}
          </Button>
        </Box>
      </Flex>
    </Flex>
  );
};
